import React, { useCallback, useState } from 'react';
import { Results } from '../Results/Results';
import { useRouteParams } from '../../../../../../RiskToleranceWorkflow/hooks/useRouteParams';
import { ErrorState } from '../../../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { FullPageLoading } from '../../../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { Detail, HiddenLeversCompleteResponse } from '../../HiddenLeversRTQ.meta';
import { use3dRTQInputRequestMutation } from '../../api/use3dRTQInputRequestMutation';
import useScript from '../../../../../../_hooks/useScript';
import { toast } from '../../../../../../CommonComponents/OPL/Toast/Toast';

interface Props {
  onFinish?(): void;
}

export const WebComponentWorkflow = ({ onFinish }: Props) => {
  const [hiddenLeversDetail, setHiddenLeversDetail] = useState<Detail | null>(null);

  const [isScriptLoaded, scriptError] = useScript(
    `${ADV.settings.endpoints.assets}js/risk_tolerance_questionnaire.js`,
  );

  const { clientId } = useRouteParams();

  const {
    mutateAsync: save3dRiskScoreMutation,
    isLoading: isSaving3dRiskScoreMutation,
    data: portfolio,
    isError: errorSaving3dRiskScoreMutation,
  } = use3dRTQInputRequestMutation();

  const onComplete = useCallback(
    async ({ detail }: HiddenLeversCompleteResponse) => {
      setHiddenLeversDetail(detail);

      try {
        await save3dRiskScoreMutation({ clientId, detail });

        if (onFinish) {
          onFinish();
        }
      } catch (err) {
        console.error(err);
        if (err instanceof Error) {
          toast.error(err.message);
        }
      }
    },
    [clientId, onFinish, save3dRiskScoreMutation],
  );

  const handleEvents = useCallback(
    element => {
      if (element) {
        element.addEventListener('complete', onComplete);
      }
    },
    [onComplete],
  );

  if (!isScriptLoaded || isSaving3dRiskScoreMutation) {
    return <FullPageLoading text="Loading" />;
  }

  if (scriptError) {
    return (
      <ErrorState
        height="100%"
        message="There was an error loading the questioner. Please try again later."
      />
    );
  }

  if (errorSaving3dRiskScoreMutation && hiddenLeversDetail) {
    return (
      <ErrorState
        height="100%"
        onAction={async () => {
          await save3dRiskScoreMutation({ clientId, detail: hiddenLeversDetail });

          if (onFinish) {
            onFinish();
          }
        }}
      />
    );
  }

  if (portfolio && hiddenLeversDetail) {
    return <Results portfolio={portfolio} onRestart={() => setHiddenLeversDetail(null)} />;
  }

  return (
    <oat-risk-tolerance-questionaire
      ref={handleEvents}
      show-nav-buttons="true"
      {...(ADV.info.Environment === 'Dev' ? { environment: 'test' } : {})} // defaults to prod
    />
  );
};
